import { Box, HStack, Text, VStack } from '@chakra-ui/react';
import * as Modal from './Modal';
import React, { FC, JSX, SVGProps } from 'react';
import { Res } from 'core/api';
import { Log } from 'core/infrastructure';
import { DialogBody, DialogFooter, DialogHeader, SvgIcon } from '../chakra-components';
import { BasicButton } from '../BasicButton';
import { Info, WarningWhite, ErrorWhite } from 'core/img';
import * as ModalHooks from './ModalHooks';
import { SystemProperties } from 'node_modules/@chakra-ui/react/dist/types/styled-system/generated/system.gen';
import Spinner from '../Spinner';
import { useEffectOnce } from '../useEffectOnce';
import { addLineBreaks } from 'core/ui';

type ModalMessageType = 'info' | 'warning' | 'error';

function MessageModal({ message, icon, modalType = 'info', close }: { message: string | JSX.Element, icon?: FC<SVGProps<SVGSVGElement>>, modalType?: ModalMessageType, close: () => void }) {

	ModalHooks.useOnDismiss(async () => {
		close();
	});

	const modalMessageTypeContent = {
		'info': {
			icon: Info,
			title: Res.Msg.MSG_MESSAGE_MODAL_INFORMATION_TITLE()
		},
		'warning': {
			icon: WarningWhite,
			title: Res.Msg.MSG_MESSAGE_MODAL_WARNING_TITLE()
		},
		'error': {
			icon: ErrorWhite,
			title: Res.Msg.MSG_MESSAGE_MODAL_ERROR_TITLE()
		}
	};

	return <VStack w={'full'} gap={'6'} justifyItems={'center'} bg={{ base: 'white', _dark: '{colors.neutral.900}' }}>
		<VStack gap={'4'} w={'full'}>
			<DialogHeader paddingX={'4'} paddingY={'2.5'} h={'9'} bg={modalType} width={'full'}>
				<HStack gap={'3'} color={'white'}>
					<SvgIcon src={modalMessageTypeContent[modalType].icon} w={'4'} h={'4'} />
					<Text fontWeight={'semibold'} fontSize={'sm'}>{modalMessageTypeContent[modalType].title}</Text>
				</HStack>
			</DialogHeader>
			<DialogBody p={'0'} paddingX={'4'} width={'full'} maxHeight={'60dvh'} overflowY={'auto'}>
				<VStack width={'full'} gap={'4'}>
					{icon &&
						<SvgIcon src={icon} w={'12'} h={'12'} />
					}
					<Box fontSize={'sm'} textAlign={'center'} width={'full'}>
						{typeof message === 'string'
							?
							<Text>
								{addLineBreaks(message)}
							</Text>
							: message
						}
					</Box>
				</VStack>
			</DialogBody>
		</VStack>
		<VStack w={'full'} paddingBottom={'4'} paddingX={'4'}>
			<DialogFooter width={'full'} p={'0'}>
				<HStack width={'full'} justifyContent={'center'}>
					<BasicButton w={'27.5'} h={'8'} type='success' text={Res.Common.OK_BTN()} onClick={() => close()} />
				</HStack>
			</DialogFooter>
		</VStack>
	</VStack>;
}

export async function show(message: string | JSX.Element, params?: { icon?: FC<SVGProps<SVGSVGElement>>, modalType?: ModalMessageType }): Promise<Result> {
	Log.debug(`messageBox["${typeof message === 'string' ? message : 'JSX.Element'}"].show()`);
	return Modal.show(MessageModal, { message, placement: 'top', size: 'sm', closeOnInteractOutside: false, ...params, });
}

type ButtonType = React.ComponentProps<typeof BasicButton>['type'];

type ConfirmModalProps = {
	message: string;
	icon?: FC<SVGProps<SVGSVGElement>>;
	iconColor?: SystemProperties['color'];
	btnOkLabel?: string;
	btnOkType?: ButtonType;
	btnCancelLabel?: string;
	btnCancelType?: ButtonType;
};

function ConfirmModal({ message, icon, btnOkLabel, btnOkType, btnCancelLabel, btnCancelType, close, cancel, iconColor }: ConfirmModalProps & Modal.Actions<void>) {

	const buttonWidth: React.ComponentProps<typeof BasicButton>['w'] = {
		'base': '30',
		'sm': '36'
	};

	return <VStack w={'full'} padding={'6'} gap={'6'} justifyItems={'center'} bg={{ base: 'white', _dark: '{colors.neutral.900}' }}>
		<VStack gap={'4'} w={'full'}>
			{icon && <DialogHeader p={'0'} color={iconColor}>
				<SvgIcon src={icon} w={'12'} h={'12'} />
			</DialogHeader>}
			<DialogBody p={'0'} paddingX={'4'} width={'full'} maxHeight={'60dvh'} overflowY={'auto'}>
				<Box fontSize={'sm'} textAlign={'center'} width={'full'}>
					<Text>
						{addLineBreaks(message)}
					</Text>
				</Box>
			</DialogBody>
		</VStack>
		<DialogFooter p={'0'} id='footer' display='flex' flexDirection='row' gap='9'>
			<BasicButton w={buttonWidth} type={btnCancelType ?? 'secondary'} text={btnCancelLabel || Res.Common.NO_BTN()}
				onClick={() => {
					Log.debug(`confirmBox["${message}"].close() -> No`);
					cancel();
				}}
			/>
			<BasicButton w={buttonWidth} type={btnOkType ?? 'success'} text={btnOkLabel || Res.Common.YES_BTN()}
				onClick={() => {
					Log.debug(`confirmBox["${message}"].close() -> Yes`);
					close();
				}}
			/>
		</DialogFooter>
	</VStack>;
}

export function confirm(message: string, params?: Omit<ConfirmModalProps, 'message'>): Promise<Result> {
	Log.debug(`confirmBox["${message}"].show()`);
	return Modal.show(ConfirmModal, { message, placement: 'top', size: 'sm', closeOnInteractOutside: false, ...params });
}

type WaitModalProps<T> = {
	message: string;
	icon?: FC<SVGProps<SVGSVGElement>>;
	modalType?: ModalMessageType;
	callbackFn: () => Promise<T>;
	onError?: (error: any) => void;
} & Modal.Actions<T>;

function WaitModal<T>({ message, icon, modalType = 'info', callbackFn, close, cancel, onError }: WaitModalProps<T>) {

	useEffectOnce(async () => {
		try {
			close(await callbackFn());
		} catch (err: any) {
			onError?.(err);
			cancel();
		}
	});

	const modalMessageTypeContent = {
		'info': {
			icon: Info,
			title: Res.Msg.MSG_MESSAGE_MODAL_INFORMATION_TITLE()
		},
		'warning': {
			icon: WarningWhite,
			title: Res.Msg.MSG_MESSAGE_MODAL_WARNING_TITLE()
		},
		'error': {
			icon: ErrorWhite,
			title: Res.Msg.MSG_MESSAGE_MODAL_ERROR_TITLE()
		}
	};

	return <VStack w={'full'} gap={'6'} justifyItems={'center'} bg={{ base: 'white', _dark: '{colors.neutral.900}' }}>
		<VStack gap={'4'} w={'full'}>
			<DialogHeader paddingX={'4'} paddingY={'2.5'} h={'9'} bg={modalType} width={'full'}>
				<HStack gap={'3'} color={'white'}>
					<SvgIcon src={modalMessageTypeContent[modalType].icon} w={'4'} h={'4'} />
					<Text fontWeight={'semibold'} fontSize={'sm'}>{modalMessageTypeContent[modalType].title}</Text>
				</HStack>
			</DialogHeader>
			<DialogBody p={'0'} paddingX={'4'} paddingBottom={'6'} width={'full'}>
				<VStack width={'full'} gap={'8'} alignItems={'center'}>
					{icon &&
						<SvgIcon src={icon} w={'12'} h={'12'} />
					}
					<Box fontSize={'sm'} textAlign={'center'} width={'full'}>
						<Text>
							{addLineBreaks(message)}
						</Text>
					</Box>
					<Spinner />
				</VStack>
			</DialogBody>
		</VStack>
	</VStack>;
}

type waitParams<T> = {
	message: string;
	action: () => Promise<T>;
	modalParams?: { icon?: FC<SVGProps<SVGSVGElement>>, modalType?: ModalMessageType };
	onError?: (error: any) => void;
};

export function wait<T>(params: waitParams<T>): Promise<Result<T>> {
	Log.debug(`waitBox["${params.message}"].show()`);
	return Modal.show(WaitModal, { message: params.message, callbackFn: params.action, closeOnInteractOutside: false, onError: params.onError, ...params.modalParams });
}
